import type { SignalKey } from '../../api/types'
import { SIGNAL_LABEL } from '../pipeline/labels'
import styles from './DealPanel.module.css'

/**
 * The evidence behind the verdict: what the calls confirmed, and what they did not.
 *
 * This is the product's core claim, so it gets the panel's strongest visual
 * element and nothing else is allowed to borrow it. Two rules do the work:
 *
 *   - **Chips mean evidence.** Stage, priority and commercial progress are
 *     rendered as text elsewhere (see WorkflowContext), so a chip on this panel
 *     always means "something a call did or did not establish". Previously all
 *     of it was chips in one row and a reader had no way to tell which kind of
 *     fact they were looking at.
 *   - **Solid means present, dashed means absent.** A missing signal is drawn as
 *     an unfilled outline rather than a differently-coloured fill, so "not yet"
 *     reads as an empty slot instead of a second category of finding. Colour
 *     alone would also fail anyone who cannot distinguish it.
 *
 * There is deliberately no "n of 7 confirmed" counter. It invited counting where
 * the point is reading: which signals are present is the information, and a score
 * out of seven implies the seven are equally weighted and interchangeable, which
 * the decision engine's qualification floor says they are not.
 *
 * Order is canonical (the adapter filters `QUALIFICATION_SIGNALS`), so a given
 * signal sits in the same position on every deal and the shape becomes familiar.
 */
export function SignalEvidence({
  confirmed,
  missing,
  label = 'Signals',
}: {
  confirmed: SignalKey[]
  missing: SignalKey[]
  /** Overridden by the call history, where these are one call's findings. */
  label?: string
}) {
  if (confirmed.length + missing.length === 0) return null

  return (
    <section className={styles.evidence}>
      <h3 className={styles.section}>{label}</h3>

      {confirmed.length > 0 ? (
        <>
          <p className={styles.evidenceGroup}>Confirmed</p>
          <ul className={styles.signals}>
            {confirmed.map((key) => (
              <li key={key} className={`${styles.signal} ${styles.confirmed}`}>
                <span aria-hidden="true">✓</span> {SIGNAL_LABEL[key] ?? key}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {missing.length > 0 ? (
        <>
          <p className={styles.evidenceGroup}>Missing</p>
          <ul className={styles.signals}>
            {missing.map((key) => (
              <li key={key} className={`${styles.signal} ${styles.missing}`}>
                {SIGNAL_LABEL[key] ?? key}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  )
}
